/**
 * @fileoverview SessionCard Progress Component
 * 
 * Displays a progress bar for the session card based on actual exam duration.
 * This component is separated to prevent re-rendering the entire SessionCard 
 * when progress updates.
 * 
 * @module vtc/components/SessionCard/SessionCardProgress
 */

import { useState, useEffect } from 'react';
import Card from '../Common/Card';
import useSessionStore from '../../stores/session';

/**
 * SessionCardProgress Component
 * 
 * Renders a progress bar that updates independently of the parent SessionCard.
 * Calculates progress based on:
 * - Time elapsed since test taker was redirected to exam
 * - Total exam duration from session data
 * 
 * Updates every 5 seconds to show real-time progress.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.sessionSid - Session identifier
 * @returns {JSX.Element}
 */
function SessionCardProgress({ sessionSid }) {
  const [progress, setProgress] = useState(0);
  const UPDATE_INTERVAL = 5000;

  useEffect(() => {
    /**
     * Calculate exam progress percentage
     * @returns {number} Progress percentage (0-100)
     */
    const calculateProgress = () => {
      // Get the session from the store
      const session = useSessionStore.getState().getSession(sessionSid);

      if (!session?.viewerRef?.current) {
        return 0;
      }

      try {
        // Get session data and redirected timestamp
        const sessionData = session.viewerRef.current.getSessionData();
        const redirectedAt = session.viewerRef.current.getRedirectedToExamAt();

        // If not redirected to exam yet, return 0
        if (!redirectedAt) {
          return 0;
        }

        // Get exam duration in minutes (convert to milliseconds)
        const durationMinutes = sessionData?.examConfiguration?.duration || 0;
        if (durationMinutes === 0) {
          return 0;
        }

        const durationMs = durationMinutes * 60 * 1000;

        // Calculate time elapsed since redirect
        const redirectTime = new Date(redirectedAt).getTime();
        const currentTime = Date.now();
        const elapsedMs = currentTime - redirectTime;

        // Calculate percentage (cap at 100)
        const percentage = Math.min(100, Math.round((elapsedMs / durationMs) * 100));

        return percentage;
      } catch (error) {
        console.error('[SessionCardProgress] Error calculating progress:', error);
        return 0;
      }
    };

    // Initial calculation
    setProgress(calculateProgress());

    // Update every 5 seconds
    const interval = setInterval(() => {
      setProgress(calculateProgress());
    }, UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [sessionSid]);

  return (
    <Card.Progress
      valueMax={100}
      valueNow={progress}
      themeOverride={{
        xSmallHeight: '0.2em',
      }}
    />
  );
}

export default SessionCardProgress;
